import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { fmtOrg, fmtOrgTime, sameOrgDay, tzSuffix } from '../../lib/orgTime';
import { useOrgTz } from '../../hooks/useOrgTz';
import { clearCached } from '../../lib/pageCache';
import { cn } from '../../lib/utils';

const fmt = (iso, tz) => `${sameOrgDay(iso, tz) ? fmtOrgTime(iso, tz) : fmtOrg(iso, tz)}${tzSuffix(tz)}`;

// "Data as of 10:42 · Resync" — health pages are cached for 24h per org; Resync rebuilds them now.
export default function DataAsOf({ organizationId, onResynced, className, testId = 'data-as-of' }) {
  const tz = useOrgTz();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [justNow, setJustNow] = useState(false);
  const q = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : '';
  const load = useCallback(() => { api.get(`/health-cache/status${q}`).then((r) => setSt(r.data)).catch(() => setSt(null)); }, [q]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const resync = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/health-cache/resync${q}`);
      clearCached();
      setSt(r.data); setJustNow(true); setTimeout(() => setJustNow(false), 8000);
      onResynced?.();
      toast.success('Resyncing — pages are rebuilding from the latest data');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not resync'); }
    finally { setBusy(false); }
  };

  if (st && !st.organization_id) return null;
  const label = justNow ? 'Updated just now' : st?.built_at ? `Data as of ${fmt(st.built_at, tz)}` : st ? 'Live data' : '…';
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white pl-2.5 pr-1 py-0.5 text-[11px] text-slate-600', className)} data-testid={testId} title={st?.bumped_at ? `Last resync ${fmt(st.bumped_at, tz)} · ${st.entries} cached views · refreshes every ${st.ttl_hours}h` : 'Cached for 24h; Resync rebuilds from the latest data'}>
      <span data-testid={`${testId}-label`} title={st?.reason ? `Cache v${st.version} · last rebuilt after: ${st.reason}${st.bumped_at ? ` (${fmtOrg(st.bumped_at, tz)}${tzSuffix(tz)})` : ''}` : undefined} data-reason={st?.reason || ''}>{label}</span>
      <button type="button" onClick={resync} disabled={busy} className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-60" data-testid={`${testId}-resync`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Resync
      </button>
    </span>
  );
}
